import { useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, ArrowRight, ArrowLeft, Check } from "lucide-react";
import { WhatsAppIcon } from "../components/ui-bits";
import { post, get } from "../lib/api";
import { leadSourceFields } from "../lib/leadSource";
import { measureLead } from "../lib/openaiPixel";
import { Eyebrow, MaskLine } from "../components/ui-bits";
import { Logo } from "../components/Logo";
import { PHONE_DISPLAY, PHONE_TEL, WHATSAPP_URL } from "../components/Navbar";
import { useLang } from "../i18n/LangContext";

const MONTHS = {
  en: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
  pt: ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"],
};
const WEEKDAYS = {
  en: ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"],
  pt: ["Se", "Te", "Qa", "Qi", "Se", "Sá", "Do"],
};

const field =
  "w-full rounded-lg border border-sand bg-white px-4 py-3 text-sm outline-none transition-colors placeholder:text-fog/60 focus:border-verde-700";
const labelCls = "mb-1.5 block text-xs font-medium text-ink";
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const TEAM = [
  { img: "/team-miguel.jpg", name: "Miguel" },
  { img: "/team-tomas.jpg", name: "Tomás" },
  { img: "/team-mariana.jpg", name: "Mariana" },
];

export default function Book() {
  const location = useLocation();
  const { lang, lp } = useLang();
  const T = {
    en: {
      eyebrow: "Book a meeting with our team", headline: "Book a meeting.", role: "Investment Analyst",
      sub: "Pick a day and a time — we confirm instantly when the slot is free. Times in Western Europe time (Lisbon).",
      connected: "Calendar connected — live availability is on. Pick a slot and we'll confirm instantly.",
      error: "We couldn't connect the calendar just now. You can still request a meeting below.",
      topics: ["Financing a project", "Investing with Verde", "Broker partnership", "Something else"],
      about: "What's it about? *", checking: "Checking the calendar…", time: "Preferred time *",
      none: "No free slots on this day — please pick another date.", moment: "One moment…",
      name: "Name *", email: "Email *", phone: "Phone (optional)",
      note: "Anything we should know? (optional)",
      consent: "I'm happy for Verde Capital to contact me about this meeting.",
      consentErr: "Please accept the consent to continue.",
      fieldsErr: "Please choose a topic, date and time.",
      sendErr: "Something went wrong. Please try again.",
      submit: "Request this slot", sending: "Sending…",
      doneBooked: "Meeting booked.", doneRequested: "Meeting requested.",
      doneBookedCopy: "A calendar invitation is on its way to your inbox.",
      doneRequestedCopy: "We'll confirm the exact slot by email shortly.",
      another: "Book another meeting",
      talk: "Prefer to talk?", talkCopy: "Call or message us directly — we'll find a time that suits you.",
      call: "Call us", wa: "Message us", whatsapp: "WhatsApp",
      hurry: "In a hurry?", hurryCopy: "Describe your project to Verde, our AI underwriter, and receive indicative terms within 24 hours.",
      hurryCta: "Get indicative terms",
      tz: "(Western Europe time)",
    },
    pt: {
      eyebrow: "Marcar reunião com a nossa equipa", headline: "Marcar uma reunião.", role: "Analista de Investimento",
      sub: "Escolha o dia e a hora — confirmamos instantaneamente quando o horário está livre. Horário da Europa Ocidental (Lisboa).",
      connected: "Calendário ligado — disponibilidade real ativa. Escolha um horário e confirmamos instantaneamente.",
      error: "Não foi possível ligar o calendário neste momento. Pode mesmo assim pedir uma reunião abaixo.",
      topics: ["Financiar um projeto", "Investir com a Verde", "Parceria de intermediação", "Outro assunto"],
      about: "Qual é o assunto? *", checking: "A verificar o calendário…", time: "Hora preferida *",
      none: "Sem horários livres neste dia — escolha outra data.", moment: "Um momento…",
      name: "Nome *", email: "Email *", phone: "Telefone (opcional)",
      note: "Algo que devamos saber? (opcional)",
      consent: "Autorizo a Verde Capital a contactar-me sobre esta reunião.",
      consentErr: "Por favor aceite o consentimento para continuar.",
      fieldsErr: "Escolha um assunto, data e hora.",
      sendErr: "Algo falhou. Tente novamente.",
      submit: "Pedir este horário", sending: "A enviar…",
      doneBooked: "Reunião marcada.", doneRequested: "Reunião pedida.",
      doneBookedCopy: "Um convite de calendário segue para a sua caixa de email.",
      doneRequestedCopy: "Confirmaremos o horário exato por email em breve.",
      another: "Marcar outra reunião",
      talk: "Prefere falar?", talkCopy: "Ligue ou envie-nos uma mensagem — encontraremos uma hora que lhe sirva.",
      call: "Ligar", wa: "Enviar mensagem", whatsapp: "WhatsApp",
      hurry: "Com pressa?", hurryCopy: "Descreva o seu projeto à Verde, a nossa subscritora de IA, e receba condições indicativas em 24 horas.",
      hurryCta: "Obter condições indicativas",
      tz: "(hora da Europa Ocidental)",
    },
    nl: {
      eyebrow: "Plan een gesprek met ons team", headline: "Afspraak maken.", role: "Beleggingsanalist",
      sub: "Kies dag en tijd — wij bevestigen direct wanneer het tijdvak vrij is. West-Europese tijd (Lissabon).",
      connected: "Agenda gekoppeld — echte beschikbaarheid actief. Kies een tijd en wij bevestigen direct.",
      error: "De agenda kon nu niet worden geladen. U kunt hieronder toch een gesprek aanvragen.",
      topics: ["Een project financieren", "Investeren met Verde", "Tussenpersonenpartnerschap", "Anders"],
      about: "Waar gaat het over? *", checking: "Agenda controleren…", time: "Gewenste tijd *",
      none: "Geen vrije tijden op deze dag — kies een andere datum.", moment: "Een ogenblik…",
      name: "Naam *", email: "E-mail *", phone: "Telefoon (optioneel)",
      note: "Iets dat wij moeten weten? (optioneel)",
      consent: "Ik geef Verde Capital toestemming om mij over dit gesprek te bellen of mailen.",
      consentErr: "Geef toestemming om door te gaan.",
      fieldsErr: "Kies een onderwerp, datum en tijd.",
      sendErr: "Er ging iets mis. Probeer het opnieuw.",
      submit: "Dit tijdvak aanvragen", sending: "Versturen…",
      doneBooked: "Gesprek gepland.", doneRequested: "Gesprek aangevraagd.",
      doneBookedCopy: "Een agenda-uitnodiging is onderweg naar uw inbox.",
      doneRequestedCopy: "Wij bevestigen het exacte tijdstip zo snel mogelijk per e-mail.",
      another: "Nog een gesprek plannen",
      talk: "Liever bellen?", talkCopy: "Bel of stuur ons een bericht — wij vinden een moment dat u schikt.",
      call: "Bellen", wa: "Bericht sturen", whatsapp: "WhatsApp",
      hurry: "Weinig tijd?", hurryCopy: "Beschrijf uw project aan Verde, onze AI-underwriter, en ontvang indicatieve voorwaarden binnen 24 uur.",
      hurryCta: "Indicatieve voorwaarden aanvragen",
      tz: "(West-Europese tijd)",
    },
  }[lang];
  const calStatus = new URLSearchParams(location.search).get("calendar");
  const navigate = useNavigate();
  const goBack = () => {
    if (window.history.state && window.history.state.idx > 0) navigate(-1);
    else navigate(lp("/"));
  };
  const now = new Date();
  const todayIso = iso(now);
  const minDate = iso(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1));
  const maxDate = iso(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 45));

  const [analyst] = useState(() => {
    const requested = new URLSearchParams(window.location.search).get("analyst");
    const hit = TEAM.find((t) => t.name.toLowerCase() === (requested || "").toLowerCase());
    return hit || TEAM[Math.floor(Math.random() * TEAM.length)];
  });
  const [form, setForm] = useState({ topic: "", date: "", time: "", name: "", email: "", phone: "", note: "", consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [slots, setSlots] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [booked, setBooked] = useState(false);
  const [view, setView] = useState(new Date(now.getFullYear(), now.getMonth(), 1));

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const loadSlots = async (value) => {
    setSlots(null);
    setSlotsLoading(true);
    try {
      const data = await get(`/meetings/availability?date=${value}`);
      if (data.connected) {
        setSlots(data.slots);
        setForm((f) => ({
          ...f,
          time: data.slots.some((s) => s.available && s.time === f.time) ? f.time : "",
        }));
      } else {
        setSlots(null);
      }
    } catch {
      setSlots(null);
    }
    setSlotsLoading(false);
  };

  const pickDay = (day) => {
    const d = iso(day);
    setForm((f) => ({ ...f, date: d, time: "" }));
    loadSlots(d);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.consent) return setError(T.consentErr);
    if (!form.date || !form.time || !form.topic) return setError(T.fieldsErr);
    setError("");
    setSubmitting(true);
    try {
      const res = await post("/meetings", { ...form, lang, ...leadSourceFields() });
      measureLead(res?.data?.event_id);
      setBooked(Boolean(res.data?.booked));
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : T.sendErr);
      if (typeof detail === "string" && detail.includes("taken")) loadSlots(form.date);
    }
    setSubmitting(false);
  };

  const reset = () => {
    setDone(false);
    setBooked(false);
    setSlots(null);
    setForm({ topic: "", date: "", time: "", name: "", email: "", phone: "", note: "", consent: false });
  };

  // Calendar grid (Monday-first)
  const monthStart = new Date(view.getFullYear(), view.getMonth(), 1);
  const startOffset = (monthStart.getDay() + 6) % 7;
  const daysInMonth = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
  const cells = [
    ...Array.from({ length: startOffset }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => new Date(view.getFullYear(), view.getMonth(), i + 1)),
  ];
  const prevDisabled = view.getFullYear() === now.getFullYear() && view.getMonth() === now.getMonth();
  const nextDisabled = new Date(view.getFullYear(), view.getMonth() + 1, 1) > new Date(now.getFullYear(), now.getMonth(), now.getDate() + 45);
  const SLOTS = ["09:00", "10:00", "11:00", "12:00", "14:00", "15:00", "16:00", "17:00"];

  return (
    <div className="min-h-screen bg-cream" data-testid="book-page">
      <div className="container-x flex items-center justify-between pt-7 sm:pt-9" data-testid="book-topbar">
        <Link to={lp("/")} data-testid="book-logo" aria-label="Verde Capital home">
          <Logo />
        </Link>
        <button
          type="button"
          onClick={goBack}
          data-testid="book-back"
          className="inline-flex items-center gap-2 rounded-full border border-sand bg-white px-5 py-2.5 text-sm font-semibold text-verde-800 shadow-sm transition-colors duration-300 hover:border-verde-700"
        >
          <ArrowLeft className="h-4 w-4" />
          {lang === "pt" ? "Voltar" : lang === "nl" ? "Terug" : "Back"}
        </button>
      </div>
      <section className="pt-10 sm:pt-14 pb-10">
        <div className="container-x max-w-5xl">
          <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-10 sm:gap-14 items-center">
            <div className="order-2 sm:order-1 flex flex-col items-center text-center" data-testid="book-team">
              <img
                src={analyst.img}
                alt={analyst.name}
                data-testid="book-team-photo"
                className="h-28 w-28 rounded-full object-cover ring-1 ring-verde-800/10 shadow-[0_14px_30px_-14px_rgba(7,39,29,0.55)]"
              />
              <p className="mt-3 font-display text-base font-bold leading-tight text-charcoal" data-testid="book-team-name">{analyst.name}</p>
              <p className="mt-0.5 text-xs text-fog" data-testid="book-team-role">{T.role}</p>
            </div>
            <div className="order-1 sm:order-2">
              <Eyebrow>{T.eyebrow}</Eyebrow>
              <h1 className="display mt-6 text-[2.4rem] sm:text-5xl text-charcoal" data-testid="book-headline">
                <MaskLine>{T.headline}</MaskLine>
              </h1>
              <p className="mt-5 text-ink max-w-xl">
                {T.sub}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-28">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 xl:col-span-8">
            {calStatus === "connected" && (
              <div className="mb-6 flex items-start gap-3 rounded-xl border border-verde-700/30 bg-verde-50 px-5 py-4" data-testid="calendar-banner-connected">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-verde-700" />
                <p className="text-sm text-verde-900">{T.connected}</p>
              </div>
            )}
            {calStatus === "error" && (
              <div className="mb-6 rounded-xl border border-red-700/20 bg-red-50 px-5 py-4" data-testid="calendar-banner-error">
                <p className="text-sm text-red-700">{T.error}</p>
              </div>
            )}

            {done ? (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl bg-white border border-sand p-10 text-center" data-testid="meeting-confirmation">
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-verde-800">
                  <Check className="h-5 w-5 text-mint" />
                </span>
                <p className="mt-6 font-display text-2xl font-bold text-charcoal">
                  {booked ? T.doneBooked : T.doneRequested}
                </p>
                <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-ink">
                  {form.topic} — {form.date} {T.tz} · {form.time}.{" "}
                  {booked ? T.doneBookedCopy : T.doneRequestedCopy}
                </p>
                <button
                  type="button"
                  onClick={reset}
                  data-testid="meeting-book-another"
                  className="mt-8 inline-flex items-center justify-center rounded-full bg-verde-800 px-7 py-3 text-sm font-semibold text-white transition-colors hover:bg-verde-700"
                >
                  {T.another}
                </button>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="rounded-2xl bg-white border border-sand p-6 sm:p-10 space-y-6" data-testid="meeting-form">
                <div>
                  <span className={labelCls}>{T.about}</span>
                  <div className="grid grid-cols-2 gap-2">
                    {T.topics.map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, topic: t }))}
                        data-testid={`meeting-topic-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className={`rounded-lg border px-3 py-2.5 text-left text-[13px] font-medium transition-colors ${
                          form.topic === t
                            ? "border-verde-700 bg-verde-50 text-verde-800"
                            : "border-sand bg-white text-ink hover:border-verde-700/40"
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="rounded-xl border border-sand bg-white p-4" data-testid="meeting-calendar">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-charcoal">
                      {MONTHS[lang][view.getMonth()]} {view.getFullYear()}
                    </p>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))}
                        disabled={prevDisabled}
                        aria-label={lang === "pt" ? "Mês anterior" : lang === "nl" ? "Vorige maand" : "Previous month"}
                        data-testid="cal-prev"
                        className="flex h-10 w-10 items-center justify-center rounded-full text-charcoal transition-colors hover:bg-verde-50 disabled:opacity-25 disabled:hover:bg-transparent"
                      >
                        ‹
                      </button>
                      <button
                        type="button"
                        onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))}
                        disabled={nextDisabled}
                        aria-label={lang === "pt" ? "Mês seguinte" : lang === "nl" ? "Volgende maand" : "Next month"}
                        data-testid="cal-next"
                        className="flex h-10 w-10 items-center justify-center rounded-full text-charcoal transition-colors hover:bg-verde-50 disabled:opacity-25 disabled:hover:bg-transparent"
                      >
                        ›
                      </button>
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-7 gap-1 text-center">
                    {WEEKDAYS[lang].map((w) => (
                      <span key={w} className="text-[10px] font-semibold uppercase tracking-wide text-fog">
                        {w}
                      </span>
                    ))}
                    {cells.map((day, i) =>
                      day === null ? (
                        <span key={`empty-${i}`} />
                      ) : (
                        (() => {
                          const d = iso(day);
                          const dis = d < minDate || d > maxDate || day.getDay() === 0 || day.getDay() === 6;
                          const sel = form.date === d;
                          return (
                            <button
                              key={d}
                              type="button"
                              disabled={dis}
                              onClick={() => pickDay(day)}
                              data-testid={`meeting-day-${d}`}
                              className={`h-9 rounded-full text-[13px] transition-colors ${
                                sel
                                  ? "bg-verde-800 font-semibold text-white"
                                  : dis
                                    ? "text-fog/30"
                                    : "text-charcoal hover:bg-verde-50"
                              } ${d === todayIso && !sel ? "ring-1 ring-verde-700" : ""}`}
                            >
                              {day.getDate()}
                            </button>
                          );
                        })()
                      )
                    )}
                  </div>
                </div>

                <div>
                  <span className={labelCls}>
                    {slotsLoading ? T.checking : T.time}
                  </span>
                  <div className="grid grid-cols-4 gap-2" data-testid="meeting-time">
                    {slotsLoading ? (
                      <p className="col-span-4 py-3 text-center text-xs text-fog">{T.moment}</p>
                    ) : slots && !slots.some((s) => s.available) ? (
                      <p className="col-span-4 py-3 text-center text-xs text-fog">
                        {T.none}
                      </p>
                    ) : (
                      (slots || SLOTS.map((s) => ({ time: s, available: true }))).map((s) => (
                        <button
                          key={s.time}
                          type="button"
                          disabled={s.available === false}
                          onClick={() => setForm((f) => ({ ...f, time: s.time }))}
                          data-testid={`meeting-slot-${s.time.replace(":", "-")}`}
                          className={`rounded-lg border py-2.5 text-[13px] font-medium transition-colors ${
                            form.time === s.time
                              ? "border-verde-800 bg-verde-800 text-white"
                              : s.available === false
                                ? "border-sand/60 bg-sand/20 text-fog/40 line-through"
                                : "border-sand bg-white text-charcoal hover:border-verde-700"
                          }`}
                        >
                          {s.time}
                        </button>
                      ))
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="meeting-name" className={labelCls}>{T.name}</label>
                    <input id="meeting-name" className={field} value={form.name} onChange={set("name")} data-testid="meeting-name" />
                  </div>
                  <div>
                    <label htmlFor="meeting-email" className={labelCls}>{T.email}</label>
                    <input id="meeting-email" type="email" className={field} value={form.email} onChange={set("email")} data-testid="meeting-email" />
                  </div>
                </div>
                <div>
                  <label htmlFor="meeting-phone" className={labelCls}>{T.phone}</label>
                  <input id="meeting-phone" type="tel" className={field} value={form.phone} onChange={set("phone")} data-testid="meeting-phone" />
                </div>
                <div>
                  <label htmlFor="meeting-note" className={labelCls}>{T.note}</label>
                  <textarea id="meeting-note" rows={3} className={field} value={form.note} onChange={set("note")} data-testid="meeting-note" />
                </div>

                <label className="flex items-start gap-3 cursor-pointer" data-testid="meeting-consent">
                  <input
                    type="checkbox"
                    checked={form.consent}
                    onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
                    className="mt-0.5 h-4 w-4 accent-[#114D38]"
                  />
                  <span className="text-sm text-ink">{T.consent}</span>
                </label>

                {error && <p className="text-sm text-red-700" data-testid="meeting-error">{error}</p>}

                <button
                  type="submit"
                  disabled={submitting || !form.name.trim() || !/.+@.+\..+/.test(form.email) || !form.date || !form.time || !form.topic}
                  className="group inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-verde-800 px-8 py-4 text-sm font-semibold text-white transition-all hover:bg-verde-700 disabled:cursor-not-allowed disabled:opacity-40"
                  data-testid="meeting-submit"
                >
                  {submitting ? T.sending : T.submit}
                </button>
              </form>
            )}
          </div>

          <div className="lg:col-span-5 xl:col-span-4 space-y-6 text-sm text-ink">
            <div className="border-t border-charcoal/15 pt-6" data-testid="booking-call-panel">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-verde-700">{T.talk}</p>
              <p className="mt-4 text-ink">{T.talkCopy}</p>
              <a
                href={PHONE_TEL}
                data-testid="booking-phone"
                className="mt-5 flex items-center gap-3 rounded-xl border border-sand bg-white px-5 py-4 transition-colors hover:border-verde-700"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-verde-800">
                  <Phone className="h-4 w-4 text-mint" />
                </span>
                <span>
                  <span className="block text-[11px] font-medium uppercase tracking-[0.14em] text-fog">{T.call}</span>
                  <span className="block font-display text-lg font-bold text-charcoal">{PHONE_DISPLAY}</span>
                </span>
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="booking-whatsapp"
                className="mt-3 flex items-center gap-3 rounded-xl border border-sand bg-white px-5 py-4 transition-colors hover:border-verde-700"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-verde-800">
                  <WhatsAppIcon className="h-4 w-4 text-mint" />
                </span>
                <span>
                  <span className="block text-[11px] font-medium uppercase tracking-[0.14em] text-fog">{T.whatsapp}</span>
                  <span className="block font-semibold text-charcoal">{T.wa}</span>
                </span>
              </a>
            </div>
            <div className="border-t border-charcoal/15 pt-6" data-testid="booking-apply-panel">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-verde-700">{T.hurry}</p>
              <p className="mt-4 text-ink">
                {T.hurryCopy}
              </p>
              <Link
                to={lp("/apply")}
                data-testid="booking-link-apply"
                className="mt-4 inline-flex items-center gap-1.5 font-semibold text-verde-800 hover:underline"
              >
                {T.hurryCta}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
